import React, { useState } from "react"; 
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query"; 
import {
  Container,
  Typography,
  Box,
  Link,
  Divider,
  CircularProgress,
} from "@mui/material";

// Components & Utils
import DOMPurify from "dompurify";
import RichTextContent from "../components/RichTextContext";
import axiosClient from "axiosClient";
import Socialmedia from "./Socialmedia";
import StationSidebar from "../components/StationSidebar"; 
import BlueLine from "./BlueLine";
import LinesDecor from "./LinesDecor";
import StationGallery from "../components/StationGallery"; // <--- IMPORT NEW COMPONENT

// Icons
import RoomIcon from "@mui/icons-material/Room";
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Images
import thirdimage from '../../assets/centrum_assets/design/stations/stationBG.png'

// --- HOOK IMPORT ---
import { useStations } from "../../hooks/useStations"; 

export default function StationDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const currentFilter = state?.currentFilter || "All";

  const initialStation = state?.currentStation || state?.station;
  const stationId = initialStation?.id;

  // 1. FETCH FULL LIST
  const { data: allStations = [], isLoading: listLoading } = useStations();


// 2. FETCH SINGLE STATION
  const { data: station, isLoading: stationLoading } = useQuery({
    queryKey: ['station', stationId], 
    queryFn: async () => {
      if (!stationId) return null;

      const res = await axiosClient.get(`/station-lists/${stationId}`);
      
      return res.data.data;
    },
    enabled: !!stationId, 
    initialData: initialStation,
    keepPreviousData: true, 
  });

  const loading = stationLoading || listLoading;
  const [imageLoaded, setImageLoaded] = useState(false);

  // 3. LOGIC & HANDLERS
  const galleryImages = Array.isArray(station?.gallery) ? station.gallery : [];
  const mainImage = station?.mainImage;

  const sortedStations = [...allStations].sort((a, b) =>
    new Date(b.date_opened) - new Date(a.date_opened)
  );

  const filteredList = currentFilter === "All" 
      ? sortedStations 
      : sortedStations.filter(s => s.province === currentFilter);

  const currentFilteredIndex = station ? filteredList.findIndex((s) => s.id === station.id) : -1;
  const nextPost = filteredList[currentFilteredIndex - 1]; 
  const prevPost = filteredList[currentFilteredIndex + 1];

  const sidebarList = [
    nextPost ? { ...nextPost, isCurrent: false } : null,
    { ...station, isCurrent: true },
    prevPost ? { ...prevPost, isCurrent: false } : null,
  ].filter(Boolean);

  const handleStationNavigation = (targetStation) => {
    if (targetStation) {
      setImageLoaded(false);
      navigate("/station_details", {
        state: {
          station: targetStation,
          currentStation: targetStation,
          currentFilter: currentFilter
        },
        replace: true
      });
      window.scrollTo(0, 0);
    }
  };

  // --- MAP URL LOGIC ---
  let googleMapsUrl = "#";
  if (station?.lat && station?.lon) {
    googleMapsUrl = `http://maps.google.com/?q=${station.lat},${station.lon}`;
  } else if (station?.location) {
    googleMapsUrl = `http://maps.google.com/?q=${encodeURIComponent(station.location)}`;
  }

  if (loading && !station) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!station) return <Typography sx={{ p: 4 }}>Error: Station data not found.</Typography>;

  const shareUrl = window.location.href;

  const handleFacebookClick = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fbUrl, "_blank", "width=600,height=400,noopener,noreferrer");
  };

  const handleInstagramClick = () => {
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  return (
      <>
      <Helmet>
        <meta property="og:title" content={station.location} />
        <meta property="og:description" content={station.province || "Visit our station!"} />
        <meta property="og:image" content={station.mainImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Box
        sx={{
          backgroundImage: `url(${thirdimage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "#1c1c1c",
          position: "relative",
          overflow: "hidden",
          pb: 10,
        }}
      >
        <Container sx={{ py: 6, px:4 }}>
          {/* TITLE */}
          <Typography variant="h3" fontWeight={800} sx={{ mb: 1, color:"#1c2542", fontSize: { xs: "2rem", md: "3rem" } }}>
            {station.location}
          </Typography>

          {/* MAP LINK */}
          <Link
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              color: "#7ac142",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem" },
              display: "flex",
              alignItems: "center",
              letterSpacing: 2,
              mb: 3,
            }}
          >
            <RoomIcon sx={{ fontSize: { xs: 20, md: 25 }, mr: 1 }} />
            VIEW IN MAP
          </Link>

          {/* TOP SECTION: IMAGE + SIDEBAR */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "3fr 1fr" },
              gap: 4,
              maxWidth: 1200,
              mx: "auto",
            }}
          >
            {/* --- MAIN IMAGE SECTION --- */}
            <Box
              sx={{
                position: "relative",
                height: { xs: 300, md: 500 },
                boxShadow: 4,
                overflow: "hidden",
              }}
            >
              {mainImage ? (
                <>
                  <Box
                    key={`bg-${station.id}`} 
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${mainImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(6px)", 
                      transform: "scale(1.2)",
                      zIndex: 1,
                      opacity: imageLoaded ? 1 : 0, 
                      transition: "opacity 0.8s ease-in-out",
                    }}
                  />
                  
                  {!imageLoaded && (
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3 }}>
                      <CircularProgress size={40} sx={{ color: "#123464" }} />
                    </Box>
                  )}

                  <Box
                    component="img"
                    key={station.id}
                    src={mainImage}
                    alt={station.location}
                    loading="eager"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      p: 1, 
                      opacity: imageLoaded ? 1 : 0,
                      transition: "opacity 0.8s ease-in-out",
                    }}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', opacity: 0.5, zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h6" fontWeight={600} color="text.secondary">
                    NO IMAGE AVAILABLE
                  </Typography>
                </Box>
              )}
            </Box>

            {/* RIGHT SIDE – REUSABLE SIDEBAR (DESKTOP) */}
            <StationSidebar 
               list={sidebarList}
               loading={loading}
               currentFilter={currentFilter}
               onNavigate={handleStationNavigation}
            />
          </Box>
          
          {/* Description */}
          <BlueLine mt={3}>
            <RichTextContent
              sx={{
                textAlign: 'justify',
                maxWidth: { xs: "100%", md: "75%" },
                mt: 2,
                '& p': { margin: 0, marginBottom: '1em', fontSize:{md:'1.4rem', xs: '1.7rem'}, fontWeight: 400, lineHeight: 1.7  } 
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(station.quote || "No description available."),
              }}
            />
          </BlueLine>

          {/* --- REPLACED HUGE BLOCK WITH NEW COMPONENT --- */}
          <StationGallery 
            images={galleryImages}
            quote={station.quote}
            stationName={station.location}
          />
          {/* ----------------------------------------------- */}

          {/* STATION OPENINGS LIST (Mobile Only) */}
          <StationSidebar 
             list={sidebarList}
             loading={loading}
             currentFilter={currentFilter}
             onNavigate={handleStationNavigation}
             sx={{ 
               display: { xs: "flex", md: "none" }, 
               maxWidth: { xs: "100%", sm: "75%" },
               mx: "auto",
               mb: 4
             }}
          /> 

          {/* SHARE BUTTONS */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mb: 2,
              mt: 6,
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 2, color: "#050f38ff", fontSize: { xs: "1rem", md: "1.25rem" } }}>
              SHARE OPENING
            </Typography>
            
            <FacebookIcon 
              fontSize="large" 
              sx={{ cursor: "pointer", color: "#050f38ff", "&:hover": { color: "#00b0f0" } }} 
              onClick={handleFacebookClick} 
            />
            
            <InstagramIcon 
              fontSize="large" 
              sx={{ cursor: "pointer", color: "#050f38ff", "&:hover": { color: "#00b0f0" } }} 
              onClick={handleInstagramClick} 
            />
          </Box>

          <Divider sx={{ my: 4, borderColor: "#3f3a3aff", width: { xs: '100%', sm: '100%', md: '100%', lg: '75%' }, mx: { xs: 'auto', lg: 0 } }} />

          {/* BOTTOM NAVIGATION */}
          <Box sx={{ 
              width: { xs: '100%', sm: '100%', md: '100%', lg: '75%' }, 
              mx: { xs: 'auto', lg: 0 }, 
              display: 'flex', 
              flexDirection: 'row',
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: 4,
             flexWrap: 'nowrap'
          }}>
            <Box
              sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  cursor: (prevPost && !loading) ? 'pointer' : 'default', 
                  opacity: (prevPost && !loading) ? 1 : 0.3, 
                  pointerEvents: (prevPost && !loading) ? 'auto' : 'none',
                  width: { xs: '100%', sm: 'auto' }
              }}
              onClick={() => !loading && prevPost && handleStationNavigation(prevPost)}
            >
              <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.15)', 
                      transform: 'scale(1.05)'
                  }
              }}>
                 <ArrowBackIosNewIcon sx={{ fontSize: 30, color: '#333' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, opacity: 0.7, color: "#5b5d66", fontSize: { xs: '1rem', md: '1.25rem' } }}>Previous Post</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, maxWidth: '95%', color: "#5b5d66", mt: 0.5, fontSize: { xs: '1.2rem', md: '1.5rem', "&:hover": { color: "#00b0f0" } } }}>
                  {prevPost?.location || "End of List"}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  textAlign: 'right', 
                  cursor: (nextPost && !loading) ? 'pointer' : 'default', 
                  opacity: (nextPost && !loading) ? 1 : 0.3, 
                  pointerEvents: (nextPost && !loading) ? 'auto' : 'none',
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'flex-end', sm: 'flex-end' } 
              }}
              onClick={() => !loading && nextPost && handleStationNavigation(nextPost)}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end',  }}>
                <Typography variant="h5" sx={{ fontWeight: 600, opacity: 0.7, color: "#5b5d66", fontSize: { xs: '1rem', md: '1.25rem',  } }}>Next Post</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, maxWidth: '95%', color: "#5b5d66", mt: 0.5, fontSize: { xs: '1.2rem', md: '1.5rem', "&:hover": { color: "#00b0f0" } } }}>
                  {nextPost?.location || "Latest"}
                </Typography>
              </Box>
              <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.15)',
                      transform: 'scale(1.05)'
                  }
              }}>
                 <ArrowForwardIosIcon sx={{ fontSize: 30, color: '#333' }} />
              </Box>
            </Box>
          </Box>
          
        </Container>
      </Box>
      <Socialmedia />
       <LinesDecor
        greenMt={{ xl: -6, lg: -5, md: -4, sm: -2, xs: 1 }}
        blueMt={{ xl: -0.3, lg: 1, md: 2, sm: 1, xs: 3 }}
      />
    </>
  );
}